import { FormEvent, type ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import { Check, ExternalLink, History, Library as LibraryIcon, Loader2, Pencil, Plus, Trash2, Upload, X } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useNotification } from "@/contexts/NotificationContext";
import LoadingOverlay from "@/components/overlays/LoadingOverlay";
import { Dialog } from "@/components/modals";
import { api, del, errorMessage, patch, post } from "@/lib/api";
import { uploadLearnMedia, type MediaKind } from "@/lib/media";
import {
  classifyLibraryUrl,
  DEFAULT_ANALYZE_MAX_BYTES,
  mbLabel,
  uploadKindHelp,
  uploadSizeIssue,
  URL_FIELD_HELP,
} from "@/lib/libraryIngestHints";
import { accessDeniedNavigateState } from "@/utils/accessDenied";
import "./Library.css";

type Tab = "mine" | "others" | "inbox";

type Attach = { targetKind: string; targetId: string };

interface HistoryMediaRef {
  mediaId: string;
  filename?: string;
  mimeType?: string;
  sizeBytes?: number | null;
  openUrl?: string;
}

interface HistoryEvent {
  at?: string | null;
  action: string;
  byUisUserId?: string;
  from?: HistoryMediaRef | { sourceUrl?: string } | null;
  to?: HistoryMediaRef | { sourceUrl?: string } | null;
  mediaId?: string;
  filename?: string;
  sourceUrl?: string;
  modelId?: string;
  input?: number;
  output?: number;
  total?: number;
  inputTokens?: number;
  outputTokens?: number;
}

interface LibrarySource {
  id: string;
  title: string;
  sourceKind: string;
  origin: string;
  sourceUrl: string;
  originalFilename: string;
  mimeType: string;
  ownerScope: string;
  status: string;
  ingestStatus: string;
  ingestError: string;
  lastIngestedAt: string | null;
  snapshotText: string;
  summary: string;
  tags: string[];
  taxonomyCore: string;
  taxonomyConcentration: string;
  analysisNotes: string;
  analysisEdited: boolean;
  publishWhenReady?: boolean;
  openUrl?: string;
  createdAt: string;
  attaches: Attach[];
  kindLabel: string;
  createdByUisUserId?: string;
  author?: {
    userId?: string | null;
    name?: string | null;
    schoolId?: string | null;
    schoolName?: string | null;
  } | null;
  tokens?: { input: number; output: number; total: number };
  updateHistory?: HistoryEvent[];
}

interface DetailDraft {
  title: string;
  sourceUrl: string;
  tags: string;
  taxonomyCore: string;
  taxonomyConcentration: string;
  summary: string;
  snapshotText: string;
  analysisNotes: string;
}

interface LibraryMeta {
  capabilities: {
    addSources: boolean;
    editSources: boolean;
    proposeSources: boolean;
    reviewSources: boolean;
    removeSources: boolean;
    analyzeSources: boolean;
    viewOthersSources?: boolean;
  };
  sourceKit?: {
    document?: boolean;
    image?: boolean;
    audio?: boolean;
    video?: boolean;
    url?: boolean;
    replaceFile?: boolean;
    editAnalysis?: boolean;
    documentMaxBytes?: number;
    mediaMaxBytes?: number;
  };
  ingestMode?: "sqs" | "local";
  limits?: {
    maxDocumentUploadBytes?: number;
    maxMediaUploadBytes?: number;
    maxAnalyzeAttachBytes?: number;
  };
}

function uploadKindFromFile(file: File): "document" | "image" | "audio" | "video" {
  const kind = mediaKindFor(file);
  return kind === "file" ? "document" : kind;
}

const VIDEO_SPEECH_COPY =
  "We only transcribe speech. We do not read slides or on-screen text in the video. Does this video have spoken audio?";

function sourceAnalyzeBlocked(row: Pick<LibrarySource, "sourceUrl" | "sourceKind">) {
  if (row.sourceKind !== "url") return false;
  return classifyLibraryUrl(row.sourceUrl).skip;
}

function acceptForKit(kit: NonNullable<LibraryMeta["sourceKit"]>) {
  const parts: string[] = [];
  if (kit.document) parts.push(".pdf,.txt,.doc,.docx,.md,.html,application/pdf,text/plain");
  if (kit.image) parts.push("image/*");
  if (kit.audio) parts.push("audio/*");
  if (kit.video) parts.push("video/*");
  return parts.join(",");
}

function statusLabel(status: string) {
  if (status === "proposed") return "Proposed";
  if (status === "published") return "Live";
  if (status === "archived") return "Archived";
  return "Draft";
}

function ingestBusy(status: string) {
  return status === "pending" || status === "processing" || status === "uploaded";
}

function sourceLabel(row: LibrarySource) {
  return (row.title || "").trim() || row.originalFilename || "This source";
}

function rowFileLabel(row: LibrarySource) {
  if (row.originalFilename) return `${row.kindLabel} · ${row.originalFilename}`;
  if (row.sourceUrl) {
    try {
      const host = new URL(row.sourceUrl).hostname.replace(/^www\./, "");
      return `${row.kindLabel} · ${host}`;
    } catch {
      return `${row.kindLabel} · ${row.sourceUrl}`;
    }
  }
  return row.kindLabel;
}

function isMineSource(row: LibrarySource | null, userId?: string | number | null) {
  if (!row || userId == null || userId === "") return false;
  return String(row.createdByUisUserId || "") === String(userId);
}

function othersAuthorGroups(items: LibrarySource[]) {
  const map = new Map<string, { key: string; title: string; subtitle: string | null; items: LibrarySource[] }>();
  for (const row of items) {
    const author = row.author;
    const userId = author?.userId || row.createdByUisUserId || "unknown";
    const schoolId = author?.schoolId || "platform";
    const key = `${userId}::${schoolId}`;
    const existing = map.get(key);
    if (existing) {
      existing.items.push(row);
      continue;
    }
    map.set(key, {
      key,
      title: author?.name || "Someone",
      subtitle: author?.schoolName || null,
      items: [row],
    });
  }
  return [...map.values()].sort((a, b) => {
    const as = a.subtitle === "Platform" || !a.subtitle ? "\uFFFF" : a.subtitle;
    const bs = b.subtitle === "Platform" || !b.subtitle ? "\uFFFF" : b.subtitle;
    const school = as.localeCompare(bs);
    if (school) return school;
    return a.title.localeCompare(b.title);
  });
}

function ingestCopy(row: LibrarySource, ingestMode?: "sqs" | "local") {
  if (row.ingestStatus === "ready" && row.lastIngestedAt) {
    return {
      pill: "Analyzed",
      body: `Last analyzed ${new Date(row.lastIngestedAt).toLocaleString()}. Edit anything below — the server already has this snapshot.`,
    };
  }
  if (row.ingestStatus === "failed") {
    return {
      pill: "Analysis failed",
      body: row.ingestError || "Analysis failed. You can still type tags, taxonomy, and a transcript, or retry.",
    };
  }
  if (row.ingestStatus === "skipped") {
    return {
      pill: "Skipped",
      body:
        row.ingestError ||
        "This was not analyzed. Upload a file if this was a platform or Google link.",
    };
  }
  if (row.ingestStatus === "processing") {
    return {
      pill: "Processing",
      body:
        "Speech is being transcribed. A long file can take minutes. This card stays locked until that finishes.",
    };
  }
  if (row.ingestStatus === "uploaded") {
    return {
      pill: "Uploaded",
      body: "Waiting for the analysis pipeline. This card is locked until that finishes.",
    };
  }
  if (ingestMode === "sqs") {
    return {
      pill: "Queued",
      body: "Queued for analysis. This card is locked until it finishes.",
    };
  }
  return {
    pill: "Analyzing…",
    body: "Analysis is running. This card is locked until it finishes. You can leave the page.",
  };
}

function StatusBadge({
  row,
  onToggle,
}: {
  row: LibrarySource;
  onToggle?: (live: boolean) => void;
}) {
  const live = row.status === "published";
  const label =
    row.status === "draft" && row.publishWhenReady ? "Draft" : statusLabel(row.status);
  const canToggle = Boolean(onToggle) && (row.status === "draft" || row.status === "published");
  const className = `al-lib-pill${canToggle ? " al-lib-pill--switch" : ""}${live ? " is-on" : " is-draft"}`;
  const title =
    row.status === "draft" && row.publishWhenReady
      ? "Will go live when analysis finishes"
      : live
        ? "Set back to draft"
        : "Publish to the library";
  if (!canToggle) {
    return (
      <span className={className} title={row.publishWhenReady ? title : undefined}>
        {label}
      </span>
    );
  }
  return (
    <button
      type="button"
      className={className}
      aria-pressed={live}
      title={title}
      onClick={() => onToggle?.(!live)}
    >
      {live ? "Live" : "Draft"}
    </button>
  );
}

function OpenSourceLink({ href }: { href?: string }) {
  if (!href) return null;
  return (
    <a
      className="al-lib-open"
      href={href}
      target="_blank"
      rel="noreferrer"
      title="Open in new tab"
      aria-label="Open source in a new tab"
    >
      <ExternalLink size={13} strokeWidth={2} />
    </a>
  );
}

function tokenCopy(tokens?: { input: number; output: number; total: number }) {
  if (!tokens?.total) return "";
  return `${tokens.total.toLocaleString()} tokens (${tokens.input.toLocaleString()} in · ${tokens.output.toLocaleString()} out)`;
}

function eventTokens(event: HistoryEvent) {
  const input = event.input ?? event.inputTokens ?? 0;
  const output = event.output ?? event.outputTokens ?? 0;
  const total = event.total || input + output;
  return { input, output, total };
}

function isMediaRef(value: HistoryEvent["from"]): value is HistoryMediaRef {
  return Boolean(value && typeof value === "object" && "mediaId" in value);
}

function eventWhen(event: HistoryEvent) {
  return event.at ? new Date(event.at).toLocaleString() : "";
}

function SourcePills({
  row,
  ingestMode,
  justReady,
  children,
}: {
  row: LibrarySource;
  ingestMode?: "sqs" | "local";
  justReady?: boolean;
  children?: ReactNode;
}) {
  const ingest = ingestCopy(row, ingestMode);
  const busy = ingestBusy(row.ingestStatus);
  return (
    <div className="al-lib-pills">
      <StatusBadge row={row} />
      <span className={`al-lib-pill${busy ? " is-busy" : ""}${justReady ? " is-fresh" : ""}`}>
        {busy ? <Loader2 size={11} className="al-lib-spin" /> : null}
        {ingest.pill}
      </span>
      {children}
    </div>
  );
}

function mediaKindFor(file: File): MediaKind {
  const type = (file.type || "").toLowerCase();
  if (type.startsWith("image/")) return "image";
  if (type.startsWith("video/")) return "video";
  if (type.startsWith("audio/")) return "audio";
  return "file";
}

function draftFrom(row: LibrarySource): DetailDraft {
  return {
    title: row.title || "",
    sourceUrl: row.sourceUrl || "",
    tags: (row.tags || []).join(", "),
    taxonomyCore: row.taxonomyCore || "",
    taxonomyConcentration: row.taxonomyConcentration || "",
    summary: row.summary || "",
    snapshotText: row.snapshotText || "",
    analysisNotes: row.analysisNotes || "",
  };
}

export default function Library() {
  const { capabilities, pageAccess, loading: authLoading, librarySourceKit, user } = useAuth();
  const { notice, showError, showWarning, hideNotification } = useNotification();
  const canOpen = Boolean(
    pageAccess?.library ||
      capabilities?.viewLibrary ||
      capabilities?.viewOthersSources ||
      capabilities?.addSources ||
      capabilities?.editSources ||
      capabilities?.proposeSources ||
      capabilities?.reviewSources
  );
  const fileRef = useRef<HTMLInputElement | null>(null);
  const replaceFileRef = useRef<HTMLInputElement | null>(null);
  const dirtyRef = useRef(false);
  const ingestByIdRef = useRef<Map<string, string>>(new Map());
  const ingestPrimedRef = useRef(false);
  const [freshReady, setFreshReady] = useState<Set<string>>(() => new Set());

  const [tab, setTab] = useState<Tab>("mine");
  const [items, setItems] = useState<LibrarySource[]>([]);
  const [meta, setMeta] = useState<LibraryMeta | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detail, setDetail] = useState<LibrarySource | null>(null);
  const [draft, setDraft] = useState<DetailDraft | null>(null);
  const [dirty, setDirty] = useState(false);

  const [title, setTitle] = useState("");
  const [url, setUrl] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [fileNote, setFileNote] = useState("");
  const [replaceFile, setReplaceFile] = useState<File | null>(null);
  const [replaceNote, setReplaceNote] = useState("");
  const [publishWhenReady, setPublishWhenReady] = useState(false);
  const [skipAnalyzeChoice, setSkipAnalyzeChoice] = useState(false);
  const [pendingVideo, setPendingVideo] = useState<{ file: File; target: "add" | "replace" } | null>(
    null
  );
  const [historyOpen, setHistoryOpen] = useState(false);

  const canAddSources = Boolean(meta?.capabilities.addSources ?? capabilities?.addSources);
  const canEdit = Boolean(meta?.capabilities.editSources ?? capabilities?.editSources);
  const canPropose = Boolean(meta?.capabilities.proposeSources ?? capabilities?.proposeSources);
  const canReview = Boolean(meta?.capabilities.reviewSources ?? capabilities?.reviewSources);
  const canRemove = Boolean(meta?.capabilities.removeSources ?? capabilities?.removeSources);
  const canWrite = canAddSources || canPropose;
  const kit = meta?.sourceKit || librarySourceKit || {};
  const analyzeMax = meta?.limits?.maxAnalyzeAttachBytes || DEFAULT_ANALYZE_MAX_BYTES;
  const sizeCaps = {
    documentMax: kit.documentMaxBytes,
    mediaMax: kit.mediaMaxBytes,
    analyzeMax,
  };
  const allowDocument = kit.document !== false;
  const allowImage = kit.image !== false;
  const allowAudio = kit.audio !== false;
  const allowVideo = kit.video !== false;
  const allowUrl = kit.url !== false;
  const allowReplace = kit.replaceFile !== false;
  const mineDetail = isMineSource(detail, user?.id);
  const canReplaceDetail = Boolean(
    allowReplace &&
      detail &&
      (canEdit || (mineDetail && (detail.status === "draft" || detail.status === "proposed"))),
  );
  const canAnalyze = Boolean(meta?.capabilities.analyzeSources ?? capabilities?.analyzeSources);
  const canViewOthers = Boolean(meta?.capabilities.viewOthersSources ?? capabilities?.viewOthersSources);
  const canTweakDetail = Boolean(
    detail &&
      kit.editAnalysis !== false &&
      (canEdit ||
        (mineDetail &&
          ((canPropose && detail.status === "proposed") || (canAddSources && detail.status === "draft")))),
  );
  const urlClass = !file && url.trim() ? classifyLibraryUrl(url) : classifyLibraryUrl("");
  const fileKind = file ? uploadKindFromFile(file) : null;
  const analyzeForcedOff = Boolean(url.trim() && !file && urlClass.skip);
  const skipAnalyze = !canAnalyze || analyzeForcedOff || skipAnalyzeChoice;
  const canAddFile = canWrite && (allowDocument || allowImage || allowAudio || allowVideo);
  const canAddUrl = canWrite && allowUrl;
  const canAdd = canAddFile || canAddUrl;
  dirtyRef.current = dirty;

  const openDetail = (row: LibrarySource) => {
    if (ingestBusy(row.ingestStatus)) return;
    setDetail(row);
    setDraft(draftFrom(row));
    setReplaceFile(null);
    setReplaceNote("");
    setDirty(false);
    setHistoryOpen(false);
  };

  const closeDetail = () => {
    setDetail(null);
    setDraft(null);
    setReplaceFile(null);
    setReplaceNote("");
    setDirty(false);
    setHistoryOpen(false);
  };

  const noteFreshReady = useCallback(
    (rows: LibrarySource[]) => {
      const map = ingestByIdRef.current;
      if (!ingestPrimedRef.current) {
        for (const row of rows) map.set(row.id, row.ingestStatus);
        ingestPrimedRef.current = true;
        return;
      }
      const became: LibrarySource[] = [];
      for (const row of rows) {
        const was = map.get(row.id);
        if (was && ingestBusy(was) && row.ingestStatus === "ready") became.push(row);
        map.set(row.id, row.ingestStatus);
      }
      if (!became.length) return;
      const ids = became.map((row) => row.id);
      setFreshReady((prev) => {
        const next = new Set(prev);
        ids.forEach((id) => next.add(id));
        return next;
      });
      notice({
        type: "success",
        title: "Analysis ready",
        subtext:
          became.length === 1
            ? `${sourceLabel(became[0])} is analyzed. You can review tags, summary, and transcript.`
            : `${became.length} sources are analyzed.`,
        duration: 5000,
      });
      window.setTimeout(() => {
        setFreshReady((prev) => {
          let changed = false;
          const next = new Set(prev);
          for (const id of ids) {
            if (next.delete(id)) changed = true;
          }
          return changed ? next : prev;
        });
      }, 2200);
    },
    [notice]
  );

  const applyRow = (row: LibrarySource, keepDraft: boolean) => {
    setItems((prev) => prev.map((item) => (item.id === row.id ? row : item)));
    setDetail((current) => (current?.id === row.id ? row : current));
    if (!keepDraft) {
      setDraft(draftFrom(row));
      setDirty(false);
    }
  };

  const load = useCallback(
    async (silent = false, nextTab?: Tab) => {
      if (!silent) setLoading(true);
      try {
        const which = nextTab || tab;
        const q = new URLSearchParams();
        if (which === "inbox") q.set("inbox", "1");
        else q.set("scope", which === "others" ? "others" : "mine");
        const envelope = await api<{
          success: boolean;
          data: LibrarySource[];
          meta: LibraryMeta;
        }>(`/api/library${q.toString() ? `?${q.toString()}` : ""}`);
        const next = Array.isArray(envelope.data) ? envelope.data : [];
        noteFreshReady(next);
        setItems(next);
        if (envelope.meta) setMeta(envelope.meta);
        return next;
      } catch (err) {
        notice({
          type: "error",
          title: "Could not load the Library",
          subtext: errorMessage(err),
          duration: 6000,
        });
        return [];
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [notice, tab, noteFreshReady]
  );

  useEffect(() => {
    if (authLoading || !canOpen) return;
    void load();
  }, [authLoading, canOpen, load]);

  useEffect(() => {
    if (tab === "others" && !canViewOthers) setTab("mine");
    if (tab === "inbox" && !canReview) setTab("mine");
  }, [tab, canViewOthers, canReview]);

  const waitingOnIngest =
    ingestBusy(detail?.ingestStatus || "") || items.some((row) => ingestBusy(row.ingestStatus));

  useEffect(() => {
    if (!waitingOnIngest) return;
    console.info("[library] waiting on ingest", {
      ingestMode: meta?.ingestMode || "unknown",
      hint:
        meta?.ingestMode === "sqs"
          ? "SQS is set: this browser/app will not finish analysis. Watch the Node terminal for SendMessage, then playbook step 5."
          : "Local mode: watch the Node terminal for Library analyze [id] start / extract / bedrock / ready.",
    });
    const openId = detail?.id;
    let ticks = 0;
    const timer = window.setInterval(async () => {
      const list = await load(true);
      ticks += 1;
      const still = list.filter((row) => ingestBusy(row.ingestStatus));
      if (still.length && (ticks === 1 || ticks % 4 === 0)) {
        console.info("[library] still pending", {
          ingestMode: meta?.ingestMode || "unknown",
          ids: still.map((row) => `${row.id.slice(0, 8)}=${row.ingestStatus}`),
        });
      }
      if (!openId) return;
      const fromList = list.find((row) => row.id === openId);
      if (!fromList) return;
      if (ingestBusy(fromList.ingestStatus)) {
        closeDetail();
        return;
      }
      applyRow(fromList, dirtyRef.current);
    }, 2500);
    return () => window.clearInterval(timer);
  }, [waitingOnIngest, detail?.id, load, meta?.ingestMode]);

  const resetForm = () => {
    setTitle("");
    setUrl("");
    setFile(null);
    setFileNote("");
    setPublishWhenReady(false);
    setSkipAnalyzeChoice(false);
    setPendingVideo(null);
  };

  const clearFileInput = (target: "add" | "replace") => {
    if (target === "add" && fileRef.current) fileRef.current.value = "";
    if (target === "replace" && replaceFileRef.current) replaceFileRef.current.value = "";
  };

  const offerFile = (next: File | null, target: "add" | "replace") => {
    const setNote = target === "add" ? setFileNote : setReplaceNote;
    if (!next) {
      setNote("");
      if (target === "add") setFile(null);
      else {
        setReplaceFile(null);
        setDirty(true);
      }
      return;
    }
    const kind = uploadKindFromFile(next);
    const issue = uploadSizeIssue(kind, next.size, sizeCaps);
    if (issue?.level === "block") {
      showError(issue.message, { title: "That file is too large to upload" });
      clearFileInput(target);
      setNote("");
      if (target === "add") setFile(null);
      else setReplaceFile(null);
      return;
    }
    if (kind === "video") {
      setNote(issue?.level === "warn" ? issue.message : "");
      setPendingVideo({ file: next, target });
      return;
    }
    setNote(issue?.level === "warn" ? issue.message : "");
    if (target === "add") setFile(next);
    else {
      setReplaceFile(next);
      setDirty(true);
    }
  };

  const confirmVideoYes = () => {
    if (!pendingVideo) return;
    if (pendingVideo.target === "add") setFile(pendingVideo.file);
    else {
      setReplaceFile(pendingVideo.file);
      setDirty(true);
    }
    setPendingVideo(null);
  };

  const confirmVideoNo = () => {
    if (pendingVideo?.target === "add") setFile(null);
    if (fileRef.current) fileRef.current.value = "";
    if (replaceFileRef.current) replaceFileRef.current.value = "";
    setFileNote("");
    setReplaceNote("");
    setPendingVideo(null);
  };

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!file && !url.trim()) {
      notice({ type: "error", title: "Add a file or a URL", duration: 4000 });
      return;
    }
    if (file) {
      const kind = uploadKindFromFile(file);
      const allowed =
        (kind === "document" && allowDocument) ||
        (kind === "image" && allowImage) ||
        (kind === "audio" && allowAudio) ||
        (kind === "video" && allowVideo);
      if (!allowed) {
        notice({ type: "error", title: `Your grant does not allow ${kind} uploads`, duration: 5000 });
        return;
      }
      const issue = uploadSizeIssue(kind, file.size, sizeCaps);
      if (issue?.level === "block") {
        showError(issue.message, { title: "That file is too large to upload" });
        return;
      }
    } else if (!canAddUrl) {
      notice({ type: "error", title: "Your grant does not allow URL sources", duration: 5000 });
      return;
    }
    setSaving(true);
    try {
      let mediaId: string | undefined;
      const kind = file ? "upload" : "url";
      if (file) {
        const uploaded = await uploadLearnMedia(file, mediaKindFor(file));
        mediaId = uploaded.id;
      }
      const created = await post<{ success: boolean; data: LibrarySource }>("/api/library", {
        title,
        sourceKind: kind,
        sourceUrl: kind === "url" ? url : undefined,
        mediaId,
        publishWhenReady: canEdit && publishWhenReady,
        skipAnalyze,
      });
      setDialogOpen(false);
      resetForm();
      setTab("mine");
      await load(true, "mine");
      if (created.data?.ingestStatus === "ready") {
        notice({
          type: "success",
          title: "Analysis ready",
          subtext: `${sourceLabel(created.data)} is analyzed. You can review tags, summary, and transcript.`,
          duration: 5000,
        });
      }
      if (created.data && !ingestBusy(created.data.ingestStatus)) openDetail(created.data);
    } catch (err) {
      notice({
        type: "error",
        title: "Could not add source",
        subtext: errorMessage(err),
        duration: 6000,
      });
    } finally {
      setSaving(false);
    }
  };

  const onSaveDraft = async () => {
    if (!detail || !draft) return;
    if (ingestBusy(detail.ingestStatus)) return;
    setSaving(true);
    try {
      let mediaId: string | undefined;
      if (replaceFile && canReplaceDetail && detail.sourceKind === "upload") {
        const kind = uploadKindFromFile(replaceFile);
        const issue = uploadSizeIssue(kind, replaceFile.size, sizeCaps);
        if (issue?.level === "block") {
          showError(issue.message, { title: "That file is too large to upload" });
          return;
        }
        const uploaded = await uploadLearnMedia(replaceFile, mediaKindFor(replaceFile));
        mediaId = uploaded.id;
      }
      const payload: Record<string, unknown> = {};
      if (canTweakDetail) {
        payload.title = draft.title;
        payload.tags = draft.tags;
        payload.taxonomyCore = draft.taxonomyCore;
        payload.taxonomyConcentration = draft.taxonomyConcentration;
        payload.summary = draft.summary;
        payload.snapshotText = draft.snapshotText;
        payload.analysisNotes = draft.analysisNotes;
      }
      if (canReplaceDetail && detail.sourceKind === "url") payload.sourceUrl = draft.sourceUrl;
      if (mediaId) payload.mediaId = mediaId;
      const updated = await patch<{ success: boolean; data: LibrarySource }>(`/api/library/${detail.id}`, payload);
      if (updated.data) {
        applyRow(updated.data, false);
        setReplaceFile(null);
        setReplaceNote("");
      }
      notice({ type: "success", title: "Saved your edits", duration: 2500 });
    } catch (err) {
      notice({
        type: "error",
        title: "Could not save",
        subtext: errorMessage(err),
        duration: 6000,
      });
    } finally {
      setSaving(false);
    }
  };

  const onSetPublished = async (want: boolean) => {
    if (!detail || !canEdit) return;
    setSaving(true);
    try {
      const updated = await patch<{ success: boolean; data: LibrarySource }>(`/api/library/${detail.id}`, {
        status: want ? "published" : "draft",
      });
      if (updated.data) applyRow(updated.data, true);
    } catch (err) {
      notice({
        type: "error",
        title: want ? "Could not publish" : "Could not unpublish",
        subtext: errorMessage(err),
        duration: 6000,
      });
    } finally {
      setSaving(false);
    }
  };

  const onReanalyze = async () => {
    if (!detail) return;
    setSaving(true);
    try {
      const updated = await post<{ success: boolean; data: LibrarySource }>(`/api/library/${detail.id}/analyze`, {});
      if (updated.data) {
        applyRow(updated.data, false);
        if (ingestBusy(updated.data.ingestStatus)) closeDetail();
      }
    } catch (err) {
      notice({
        type: "error",
        title: "Could not re-analyze",
        subtext: errorMessage(err),
        duration: 6000,
      });
    } finally {
      setSaving(false);
    }
  };

  const askReanalyze = () => {
    if (!detail) return;
    showWarning(
      "A new analysis replaces tags, taxonomy, summary, transcript, and notes — including anything you typed or edited by hand. Unsaved edits in this panel will also be discarded. The original file is not changed.",
      {
        title: "Re-analyze this source?",
        okButtonText: "Re-analyze",
        overlayClose: true,
        colorBackground: true,
        zIndex: 1300,
        onOkClick: () => {
          hideNotification();
          void onReanalyze();
        },
      }
    );
  };

  const onApply = async (id: string) => {
    setSaving(true);
    try {
      await post(`/api/library/${id}/apply`, {});
      await load(true);
    } catch (err) {
      notice({ type: "error", title: "Could not apply", subtext: errorMessage(err), duration: 6000 });
    } finally {
      setSaving(false);
    }
  };

  const onReject = async (id: string) => {
    if (!window.confirm("Reject and delete this proposed source?")) return;
    setSaving(true);
    try {
      await post(`/api/library/${id}/reject`, {});
      await load(true);
    } catch (err) {
      notice({ type: "error", title: "Could not reject", subtext: errorMessage(err), duration: 6000 });
    } finally {
      setSaving(false);
    }
  };

  const onRemove = async (row: LibrarySource) => {
    const published = row.status === "published";
    if (
      !window.confirm(
        published ? "Archive this published source?" : "Delete this source?"
      )
    ) {
      return;
    }
    setSaving(true);
    try {
      await del(`/api/library/${row.id}`);
      if (detail?.id === row.id) closeDetail();
      await load(true);
    } catch (err) {
      notice({ type: "error", title: "Could not remove", subtext: errorMessage(err), duration: 6000 });
    } finally {
      setSaving(false);
    }
  };

  const setDraftField = <K extends keyof DetailDraft>(key: K, value: DetailDraft[K]) => {
    setDraft((current) => (current ? { ...current, [key]: value } : current));
    setDirty(true);
  };

  if (authLoading) return <LoadingOverlay isVisible message="Loading…" />;
  if (!canOpen) {
    return <Navigate to="/" replace state={accessDeniedNavigateState("library", "/library")} />;
  }

  const detailIngest = detail ? ingestCopy(detail, meta?.ingestMode) : null;
  const tokenLine = tokenCopy(detail?.tokens);
  const historyEvents = [...(detail?.updateHistory || [])].reverse();

  return (
    <div className="al-lib">
      <LoadingOverlay isVisible={loading || saving} message={saving ? "Saving…" : "Loading…"} />

      <header className="al-lib-header">
        <div>
          <h1>
            <LibraryIcon size={20} style={{ marginRight: 8, verticalAlign: -3 }} />
            Library
          </h1>
          <p className="al-lib-sub">
            {tab === "others"
              ? "Sources other people in your scope uploaded. Not a shared school shelf yet."
              : tab === "inbox"
                ? "Proposed sources waiting for a reviewer."
                : `Add a file or an article URL. We always keep your original. Over ${mbLabel(analyzeMax)}, the AI reads a smaller image or extracted text — you can still type tags, a transcript, and a summary yourself. We do not watch videos or open Google Slides links.`}
          </p>
        </div>
        <div className="al-lib-actions">
          {canAdd && tab !== "others" && (
            <button type="button" className="al-lib-add" onClick={() => { resetForm(); setDialogOpen(true); }}>
              <Plus size={14} />
              {canAddSources ? "Add source" : "Propose source"}
            </button>
          )}
        </div>
      </header>

      {(canReview || canViewOthers) && (
        <div className="al-lib-tabs" role="tablist">
          <button
            type="button"
            className="al-lib-tab"
            role="tab"
            aria-selected={tab === "mine"}
            onClick={() => setTab("mine")}
          >
            My sources
          </button>
          {canViewOthers && (
            <button
              type="button"
              className="al-lib-tab"
              role="tab"
              aria-selected={tab === "others"}
              onClick={() => setTab("others")}
            >
              Others&apos; sources
            </button>
          )}
          {canReview && (
            <button
              type="button"
              className="al-lib-tab"
              role="tab"
              aria-selected={tab === "inbox"}
              onClick={() => setTab("inbox")}
            >
              Inbox
            </button>
          )}
        </div>
      )}

      {!loading && items.length === 0 && (
        <div className="al-lib-empty">
          {tab === "inbox"
            ? "No proposed sources waiting."
            : tab === "others"
              ? "Nobody else in your scope has a Library source yet."
              : "Nothing of yours yet. Upload a PDF, image, audio, or video, or paste an article URL."}
        </div>
      )}

      {items.length > 0 &&
        (tab === "others" ? othersAuthorGroups(items) : [{ key: "all", title: null as string | null, subtitle: null as string | null, items }]).map(
        (group) => (
          <section key={group.key} className="al-lib-group">
            {group.title ? <h2 className="al-lib-group-title">{group.title}</h2> : null}
            {group.subtitle ? <p className="al-lib-group-sub">{group.subtitle}</p> : null}
            <div className="al-lib-list">
              {group.items.map((row) => {
                const busy = ingestBusy(row.ingestStatus);
                const justReady = freshReady.has(row.id);
                const mine = isMineSource(row, user?.id);
                const showPencil =
                  canEdit ||
                  (mine &&
                    ((canPropose && row.status === "proposed") || (canAddSources && row.status === "draft")));
                const showTrash = canRemove || (mine && (row.status === "draft" || row.status === "proposed"));
                return (
                  <article
                    key={row.id}
                    className={`al-lib-row${busy ? " is-busy" : ""}${justReady ? " is-fresh" : ""}`}
                    aria-busy={busy}
                  >
                    <div className="al-lib-row-main">
                      <button
                        type="button"
                        className="al-lib-title-btn"
                        onClick={() => openDetail(row)}
                        disabled={busy}
                        title={busy ? "Analysis in progress" : row.title || "Untitled"}
                      >
                        {row.title || "Untitled"}
                      </button>
                      <span className="al-lib-row-file">{rowFileLabel(row)}</span>
                    </div>
                    <SourcePills row={row} ingestMode={meta?.ingestMode} justReady={justReady} />
                    <div className="al-lib-row-actions">
                      {showPencil && (
                        <button
                          type="button"
                          className="al-lib-icon-btn"
                          title={busy ? "Analysis in progress" : "Edit analysis"}
                          aria-label={busy ? "Analysis in progress" : "Edit this source"}
                          onClick={() => openDetail(row)}
                          disabled={busy}
                        >
                          {busy ? <Loader2 size={15} className="al-lib-spin" /> : <Pencil size={15} />}
                        </button>
                      )}
                      {canReview && row.status === "proposed" && (
                        <>
                          <button
                            type="button"
                            className="al-lib-icon-btn"
                            title="Apply"
                            aria-label="Apply proposal"
                            onClick={() => onApply(row.id)}
                          >
                            <Check size={16} />
                          </button>
                          <button
                            type="button"
                            className="al-lib-icon-btn is-danger"
                            title="Reject"
                            aria-label="Reject proposal"
                            onClick={() => onReject(row.id)}
                          >
                            <X size={16} />
                          </button>
                        </>
                      )}
                      {showTrash && (
                        <button
                          type="button"
                          className="al-lib-icon-btn is-danger"
                          title={row.status === "published" ? "Archive" : "Delete"}
                          aria-label={row.status === "published" ? "Archive this source" : "Delete this source"}
                          onClick={() => onRemove(row)}
                        >
                          <Trash2 size={15} />
                        </button>
                      )}
                    </div>
                  </article>
                );
              })}
            </div>
          </section>
        )
      )}

      <Dialog
        open={dialogOpen}
        title={canAddSources ? "Add source" : "Propose source"}
        subtitle="Upload a file, or paste an article URL. Large files stay original; the AI uses a smaller copy or extracted text. You can always tag and transcribe by hand. Platform and Google links are skipped."
        onClose={() => {
          setDialogOpen(false);
          resetForm();
        }}
        width="36rem"
      >
        <form className="al-lib-form al-lib-add-form" onSubmit={onSubmit}>
          <label>
            Title
            <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Optional" />
          </label>
          <div className="al-lib-kinds">
            {canAddFile && (
            <div className={`al-lib-kind${file ? " is-set" : ""}`}>
              <span className="al-lib-field-label">Upload</span>
              <button
                type="button"
                className="al-lib-drop"
                onClick={() => fileRef.current?.click()}
                onDragOver={(event) => event.preventDefault()}
                onDrop={(event) => {
                  event.preventDefault();
                  const next = event.dataTransfer.files?.[0];
                  if (next) offerFile(next, "add");
                }}
              >
                <Upload size={16} />
                <span>{file ? file.name : "Drop or click"}</span>
              </button>
              <input
                ref={fileRef}
                type="file"
                hidden
                accept={acceptForKit(kit)}
                onChange={(e) => offerFile(e.target.files?.[0] || null, "add")}
              />
              {fileNote ? (
                <p className="al-lib-banner is-warn">{fileNote}</p>
              ) : (
                <p className="al-lib-hint">{uploadKindHelp(fileKind, sizeCaps)}</p>
              )}
            </div>
            )}
            {canAddUrl && (
            <div className={`al-lib-kind${url.trim() ? " is-set" : ""}`}>
              <label>
                URL
                <input
                  type="url"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://"
                />
              </label>
              {urlClass.id === "empty" ? (
                <ul className="al-lib-help">
                  {URL_FIELD_HELP.map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
              ) : (
                <p className={`al-lib-banner${urlClass.skip ? " is-warn" : ""}`}>{urlClass.message}</p>
              )}
            </div>
            )}
          </div>
          <div className="al-lib-add-opts">
            {canEdit && (
              <label className="al-lib-check">
                <input
                  type="checkbox"
                  checked={publishWhenReady}
                  onChange={(e) => setPublishWhenReady(e.target.checked)}
                />
                <span className="al-lib-check-copy">
                  Publish when analyzed
                  <span className="al-lib-check-hint">
                    {skipAnalyze
                      ? "Publishes now — analysis is off, so there is nothing to wait for. Needs Edit Library Sources."
                      : "Stays a private draft until analysis finishes, then it joins the library (source of truth). Leave off to keep working on it. Needs Edit Library Sources."}
                  </span>
                </span>
              </label>
            )}
            {canAnalyze && (
              <label className="al-lib-check">
                <input
                  type="checkbox"
                  checked={skipAnalyze}
                  disabled={analyzeForcedOff}
                  onChange={(e) => setSkipAnalyzeChoice(e.target.checked)}
                />
                <span className="al-lib-check-copy">
                  Skip AI analysis
                  <span className="al-lib-check-hint">
                    {analyzeForcedOff
                      ? urlClass.message || "This URL cannot be analyzed. Upload a file instead."
                      : "You'll add tags and a transcript yourself. Needs Analyze Sources to run analysis."}
                  </span>
                </span>
              </label>
            )}
            {!canAnalyze && canWrite ? (
              <p className="al-lib-hint">
                Your grant does not include Analyze Sources, so AI analysis is off. Add tags and a
                transcript after you save.
              </p>
            ) : null}
            {canAddSources && !canEdit ? (
              <p className="al-lib-hint">
                Saved as a draft on My sources. Publishing needs Edit Library Sources.
              </p>
            ) : null}
            {!canAddSources && canPropose && (
              <p className="al-lib-hint">Proposed until a reviewer applies it to the library.</p>
            )}
          </div>
          <div className="al-lib-form-actions">
            <button
              type="button"
              className="al-lib-tab"
              onClick={() => {
                setDialogOpen(false);
                resetForm();
              }}
            >
              Cancel
            </button>
            <button type="submit" className="al-lib-scope" aria-pressed="true">
              {canAddSources ? "Add" : "Propose"}
            </button>
          </div>
        </form>
      </Dialog>

      <Dialog
        open={Boolean(detail)}
        title={detail?.title || "Source"}
        onClose={closeDetail}
        width="36rem"
        escapeClose={!historyOpen}
        subtitle={
          detail ? (
            <div className="al-lib-detail-meta">
              <StatusBadge
                row={detail}
                onToggle={
                  canEdit && (detail.status === "draft" || detail.status === "published")
                    ? (live) => void onSetPublished(live)
                    : undefined
                }
              />
              {tokenLine ? (
                <span className="al-lib-pill" title={tokenLine}>
                  {detail.tokens?.total.toLocaleString()} tokens
                </span>
              ) : null}
              {historyEvents.length > 0 ? (
                <button
                  type="button"
                  className={`al-lib-icon-btn${historyOpen ? " is-on" : ""}`}
                  aria-label="History"
                  title="History"
                  onClick={() => setHistoryOpen(true)}
                >
                  <History size={14} strokeWidth={2} />
                </button>
              ) : null}
            </div>
          ) : undefined
        }
      >
        {detail && detailIngest && draft && (
          <div className="al-lib-form">
            <p className={`al-lib-banner${detail.ingestStatus === "failed" ? " is-bad" : ""}`}>
              {ingestBusy(detail.ingestStatus) ? <Loader2 size={14} className="al-lib-spin" /> : null}
              {detailIngest.body}
            </p>
            {!canEdit && (canPropose || canAddSources) ? (
              <p className="al-lib-hint">
                {detail.status === "proposed"
                  ? "Proposed until a reviewer applies it to the library."
                  : canAddSources && detail.status === "draft"
                    ? "This is your draft. Publishing needs Edit Library Sources."
                    : "Publishing needs Edit Library Sources."}
              </p>
            ) : null}
            {(detail.sourceKind === "url" &&
              classifyLibraryUrl(detail.sourceUrl).skip &&
              !draft.snapshotText.trim()) ||
            ((detail.mimeType.startsWith("video/") || detail.mimeType.startsWith("audio/")) &&
              detail.ingestStatus === "skipped" &&
              !draft.snapshotText.trim()) ? (
              <p className="al-lib-hint">
                {detail.sourceKind === "url"
                  ? classifyLibraryUrl(detail.sourceUrl).message
                  : "No transcript. If this file had no spoken audio, analysis will not succeed. You can paste a transcript below."}
              </p>
            ) : null}
            <label>
              Title
              <input value={draft.title} onChange={(e) => setDraftField("title", e.target.value)} disabled={!canTweakDetail} />
            </label>
            {detail.sourceKind === "url" && (
              <label>
                <span className="al-lib-file-row">
                  URL
                  <OpenSourceLink href={detail.openUrl || draft.sourceUrl || detail.sourceUrl} />
                </span>
                <input
                  type="url"
                  value={draft.sourceUrl}
                  onChange={(e) => setDraftField("sourceUrl", e.target.value)}
                  disabled={!canReplaceDetail}
                />
              </label>
            )}
            {detail.sourceKind === "upload" && (detail.originalFilename || canReplaceDetail || allowReplace) ? (
              <div className="al-lib-file-row">
                <p className="al-lib-hint">
                  {replaceFile
                    ? `Replacing with ${replaceFile.name}`
                    : detail.originalFilename
                      ? `File: ${detail.originalFilename}`
                      : "No file"}
                </p>
                {!replaceFile ? <OpenSourceLink href={detail.openUrl} /> : null}
                {canReplaceDetail ? (
                  <>
                    <button
                      type="button"
                      className="al-lib-replace-link"
                      onClick={() => replaceFileRef.current?.click()}
                    >
                      {replaceFile ? "Change" : "Replace"}
                    </button>
                    <input
                      ref={replaceFileRef}
                      type="file"
                      hidden
                      accept={acceptForKit(kit)}
                      onChange={(e) => offerFile(e.target.files?.[0] || null, "replace")}
                    />
                  </>
                ) : null}
              </div>
            ) : null}
            {replaceNote ? <p className="al-lib-banner is-warn">{replaceNote}</p> : null}
            {!canReplaceDetail && detail.sourceKind === "upload" && allowReplace ? (
              <p className="al-lib-hint">Replacing this file needs Edit Library Sources.</p>
            ) : !allowReplace && detail.sourceKind === "upload" ? (
              <p className="al-lib-hint">Your grant can edit tags and transcript, not replace this file.</p>
            ) : null}
            <label>
              Tags
              <input
                value={draft.tags}
                onChange={(e) => setDraftField("tags", e.target.value)}
                placeholder="comma, separated, tags"
                disabled={!canTweakDetail}
              />
            </label>
            <div className="al-lib-kinds">
              <label>
                Taxonomy · Core
                <input
                  value={draft.taxonomyCore}
                  onChange={(e) => setDraftField("taxonomyCore", e.target.value)}
                  placeholder="e.g. Mathematics"
                  disabled={!canTweakDetail}
                />
              </label>
              <label>
                Taxonomy · Concentration
                <input
                  value={draft.taxonomyConcentration}
                  onChange={(e) => setDraftField("taxonomyConcentration", e.target.value)}
                  placeholder="e.g. Algebra I"
                  disabled={!canTweakDetail}
                />
              </label>
            </div>
            <label>
              Summary
              <textarea
                rows={3}
                value={draft.summary}
                onChange={(e) => setDraftField("summary", e.target.value)}
                disabled={!canTweakDetail}
              />
            </label>
            <label>
              Transcript / snapshot
              <textarea
                rows={8}
                value={draft.snapshotText}
                onChange={(e) => setDraftField("snapshotText", e.target.value)}
                placeholder="The text the AI will search. Paste a transcript here if the extractor could not."
                disabled={!canTweakDetail}
              />
            </label>
            <label>
              Notes
              <textarea
                rows={2}
                value={draft.analysisNotes}
                onChange={(e) => setDraftField("analysisNotes", e.target.value)}
                disabled={!canTweakDetail}
              />
            </label>
            {(canTweakDetail || (canReplaceDetail && Boolean(replaceFile))) && (
              <div className="al-lib-form-actions">
                {canTweakDetail && canAnalyze && !sourceAnalyzeBlocked(detail) && (
                <button type="button" className="al-lib-tab" onClick={askReanalyze}>
                  Re-analyze
                </button>
                )}
                <button type="button" className="al-lib-scope" aria-pressed="true" onClick={() => void onSaveDraft()} disabled={!dirty}>
                  Save edits
                </button>
              </div>
            )}
          </div>
        )}
      </Dialog>

      <Dialog
        open={Boolean(detail) && historyOpen}
        title="History"
        onClose={() => setHistoryOpen(false)}
        width="28rem"
      >
        {detail ? (
          <div className="al-lib-history">
            {historyEvents.map((event, index) => {
              const when = eventWhen(event);
              const usage = tokenCopy(eventTokens(event));
              const fromFile = isMediaRef(event.from) ? event.from : null;
              const toFile = isMediaRef(event.to) ? event.to : null;
              const fromUrl = event.from && "sourceUrl" in event.from ? event.from.sourceUrl : "";
              const toUrl = event.to && "sourceUrl" in event.to ? event.to.sourceUrl : "";
              return (
                <div key={`${event.action}-${event.at || index}`} className="al-lib-history-item">
                  {when ? <p className="al-lib-history-when">{when}</p> : null}
                  {event.action === "analyze" ? (
                    <p>
                      Analyzed
                      {usage ? ` · ${usage}` : ""}
                    </p>
                  ) : null}
                  {event.action === "replace_file" ? (
                    <p className="al-lib-history-files">
                      Replaced file
                      {fromFile ? (
                        <span className="al-lib-prior-item">
                          {fromFile.filename || "file"}
                          <OpenSourceLink href={fromFile.openUrl} />
                        </span>
                      ) : null}
                      {toFile ? (
                        <>
                          <span aria-hidden="true">→</span>
                          <span className="al-lib-prior-item">
                            {toFile.filename || "file"}
                            <OpenSourceLink href={toFile.openUrl} />
                          </span>
                        </>
                      ) : null}
                    </p>
                  ) : null}
                  {event.action === "replace_url" ? (
                    <p className="al-lib-history-files">
                      Replaced URL
                      {fromUrl ? (
                        <span className="al-lib-prior-item">
                          <span className="al-lib-history-url" title={fromUrl}>{fromUrl}</span>
                          <OpenSourceLink href={fromUrl} />
                        </span>
                      ) : null}
                      {toUrl ? (
                        <>
                          <span aria-hidden="true">→</span>
                          <span className="al-lib-prior-item">
                            <span className="al-lib-history-url" title={toUrl}>{toUrl}</span>
                            <OpenSourceLink href={toUrl} />
                          </span>
                        </>
                      ) : null}
                    </p>
                  ) : null}
                </div>
              );
            })}
          </div>
        ) : null}
      </Dialog>

      <Dialog
        open={Boolean(pendingVideo)}
        title="Spoken audio?"
        subtitle={pendingVideo?.file.name}
        onClose={confirmVideoNo}
        width="28rem"
        escapeClose
      >
        <p className="al-lib-banner">{VIDEO_SPEECH_COPY}</p>
        <div className="al-lib-form-actions">
          <button type="button" className="al-lib-tab" onClick={confirmVideoNo}>
            No — cancel
          </button>
          <button type="button" className="al-lib-scope" aria-pressed="true" onClick={confirmVideoYes}>
            Yes, it has speech
          </button>
        </div>
      </Dialog>
    </div>
  );
}
